import { useId, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { useClickOutside } from '@/hooks/useClickOutside'

interface SelectOption {
  value: string
  label: string
}

interface CustomSelectProps {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  label: string
  placeholder: string
  required?: boolean
  error?: string
}

export const CustomSelect = ({
  value,
  options,
  onChange,
  label,
  placeholder,
  required = false,
  error,
}: CustomSelectProps) => {
  const id = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const selectedIndex = options.findIndex((option) => option.value === value)
  const [activeIndex, setActiveIndex] = useState(Math.max(0, selectedIndex))
  const selected = options[selectedIndex]

  useClickOutside(rootRef, () => setOpen(false))

  const openMenu = () => {
    setActiveIndex(Math.max(0, selectedIndex))
    setOpen(true)
  }

  const choose = (index: number) => {
    const option = options[index]
    if (!option) return
    onChange(option.value)
    setActiveIndex(index)
    setOpen(false)
    buttonRef.current?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        openMenu()
        return
      }
      const direction = event.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((index) => (index + direction + options.length) % options.length)
      return
    }
    if ((event.key === 'Enter' || event.key === ' ') && open) {
      event.preventDefault()
      choose(activeIndex)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-options`}
        aria-required={required}
        aria-invalid={Boolean(error)}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-11 w-full items-center justify-between gap-3 rounded-lg border bg-white px-4 text-left text-[0.86rem] transition-all',
          'focus:outline-none focus:ring-2 focus:ring-green-600/20',
          open
            ? 'border-green-600 ring-2 ring-green-600/10'
            : 'border-base-200 hover:border-base-300',
          error && 'border-red-500 ring-2 ring-red-500/10',
          selected ? 'text-base-900' : 'text-base-400',
        )}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <svg
          className={cn(
            'h-4 w-4 shrink-0 text-base-500 transition-transform',
            open && 'rotate-180',
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          id={`${id}-options`}
          role="listbox"
          aria-label={label}
          className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-base-200 bg-white p-1.5 shadow-elevated"
        >
          {options.map((option, index) => {
            const isSelected = option.value === value
            const isActive = index === activeIndex
            return (
              <button
                key={option.value || 'placeholder'}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(index)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-[0.82rem] transition-colors',
                  isActive && 'bg-base-50',
                  isSelected ? 'font-medium text-green-700' : 'text-base-700',
                )}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <svg
                    className="h-4 w-4 shrink-0 text-green-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.29a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4A1 1 0 014.704 9.29L8 12.586l7.296-7.296a1 1 0 011.408 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}

      {error && <p className="mt-1.5 text-[0.7rem] text-red-600">{error}</p>}
    </div>
  )
}
